import { useCallback } from 'react';
import { css } from '@emotion/react';
import {
  PaymentMethod,
  PaymentPlanConfirmationStatus,
  PaymentPlanDetailsResponse,
  PaymentPlanStatus,
  updatePaymentPlan,
} from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { PaymentMethodName, usePaymentPlanCardOnFile } from '@frontend/payments-card-on-file';
import { PaymentsFlowType, StripeElementsWrapper } from '@frontend/payments-stripe-wrapper';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Modal, Text, useModalControl, useAlert } from '@frontend/design-system';
import { usePaymentPlanShallowStore } from '../../../providers/payment-plans-provider';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { isoDateToDisplayLocalString, prettify } from '../../../utils/date-helpers';
import { CancelPaymentPlanModal } from '../../PaymentPlanModals/cancel-payment-plan-modal';
import { DeletePlanModal } from '../../PaymentPlanModals/DeletePlanModal';
import { EditBillingMethodModal } from '../../PaymentPlanModals/EditBillingMethodModal';
import { EditPaymentPlanModal } from '../../PaymentPlanModals/EditPaymentPlanModal';
import { OneTimePaymentModal } from '../../PaymentPlanModals/OneTimePaymentModal';
import { PausePlanModal } from '../../PaymentPlanModals/PausePlanModal';
import { PaymentPlanStatusChip } from '../../PaymentPlanStatusChip';
import { AlertBanner } from '../atoms/alert-banner';
import { ActivatePlanAlert } from '../molecules/activate-plan-alert';
import { PaymentPlanSummaryLabel } from './payment-plan-summary-label';
import { PaymentPlanDetailsMenu } from './plan-menu';

type PaymentPlanSummaryProps = {
  planDetails: PaymentPlanDetailsResponse;
  planStatus: PaymentPlanStatus | undefined;
  handlePrint: () => void;
};

const hasRemainingBalance = (status: PaymentPlanStatus) =>
  status !== PaymentPlanStatus.Canceled && status !== PaymentPlanStatus.Completed;

export const PaymentPlanSummary = ({ planDetails, planStatus, handlePrint }: PaymentPlanSummaryProps) => {
  const { t } = useTranslation('payments');
  const { setWorkflowId } = usePaymentPlanShallowStore('setWorkflowId');
  const alert = useAlert();
  const navigate = useNavigate();
  const { modalProps: editPlanModalProps, triggerProps: editPlanTriggerProps } = useModalControl();
  const { modalProps: editBillingModalProps, triggerProps: editBillingTriggerProps } = useModalControl();
  const { modalProps: pausePlanModalProps, triggerProps: pausePlanTriggerProps } = useModalControl();
  const { modalProps: cancelPlanModalProps, triggerProps: cancelPlanTriggerProps } = useModalControl();
  const { modalProps: oneTimePaymentModalProps, triggerProps: oneTimePaymentTriggerProps } = useModalControl();
  const { modalProps: deletePlanModalProps, triggerProps: deletePlanTriggerProps } = useModalControl();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });

  const { editBillingMethodProps } = usePaymentPlanCardOnFile({
    locationId: planDetails.locationId,
    paymentAmount: planDetails.schedule.paymentAmount,
    paymentFrequency: planDetails.schedule.frequency,
    patientId: planDetails.person.id,
    patientEmail: planDetails.person.email || '',
  });

  const isPendingCustomerApproval =
    planDetails.status === PaymentPlanStatus.Paused &&
    planDetails.confirmationDetails.confirmationStatus === PaymentPlanConfirmationStatus.Pending;

  const isPausedPlan =
    planDetails.status === PaymentPlanStatus.Paused &&
    planDetails.confirmationDetails.confirmationStatus === PaymentPlanConfirmationStatus.PausedByOffice;

  const handleSetBillingMethod = async (paymentMethod: PaymentMethod) => {
    try {
      const {
        data: { workflowId },
      } = await updatePaymentPlan(planDetails.id, {
        paymentMethodId: paymentMethod.id,
        status: PaymentPlanStatus.Paused,
        notes: 'billing method updated',
      });
      alert.success(t('Payment method successfully updated.'));

      editBillingModalProps.onClose();
      setWorkflowId(workflowId);
    } catch (err) {
      alert.error(t('Error updating billing method.'));
    }
  };

  const getNextPayment = (planDetails: PaymentPlanDetailsResponse) => {
    if (planDetails.paymentSummary.remainingTerms === 1) {
      return formatCentsToCurrency(planDetails.schedule.lastPaymentAmount);
    } else if (hasRemainingBalance(planDetails.status)) {
      return formatCentsToCurrency(planDetails.schedule.paymentAmount || 0);
    } else {
      return 'N/A';
    }
  };

  const onPlanDeleted = useCallback(() => {
    navigate({ to: '/payments/payment-plans', search: { updateList: true } });
  }, [navigate]);

  const onPlanUpdated = useCallback(
    (workflowId: string) => {
      setWorkflowId(workflowId);
    },
    [setWorkflowId]
  );

  const onPlanPaused = useCallback(
    (workflowId: string) => {
      setWorkflowId(workflowId);
    },
    [setWorkflowId]
  );

  return (
    <>
      <div
        css={css`
          padding: ${theme.spacing(4, 1, 0)};
          border-top: 1px solid ${theme.colors.neutral20};
        `}
      >
        {!isMobile && (
          <>
            <ActivatePlanAlert planDetails={planDetails} />
            {planStatus === PaymentPlanStatus.Failed && <AlertBanner>{planDetails.statusReason}</AlertBanner>}
          </>
        )}
        <div
          css={css`
            margin-bottom: ${theme.spacing(isMobile ? 1 : 4)};
            display: flex;
            flex-flow: row;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-flow: row;
              gap: ${theme.spacing(isMobile ? 0 : 2)};
              ${isMobile && 'flex-direction: column;'}
            `}
          >
            {isMobile && <Text color='subdued'>{t('Name')}</Text>}
            <Text
              weight='bold'
              css={css`
                font-size: ${theme.fontSize(18)};
              `}
            >
              {planDetails.name}
            </Text>
            {!isMobile && <PaymentPlanStatusChip paymentPlanStatus={planStatus} />}
          </div>
          <PaymentPlanDetailsMenu
            planStatus={planStatus}
            confirmationStatus={planDetails?.confirmationDetails.confirmationStatus}
            handlePrint={handlePrint}
            editPlanTrigger={editPlanTriggerProps}
            editBillingTriggerProps={editBillingTriggerProps}
            pausePlanTriggerProps={pausePlanTriggerProps}
            cancelPlanTriggerProps={cancelPlanTriggerProps}
            oneTimePaymentTriggerProps={oneTimePaymentTriggerProps}
            hasPaymentMade={planDetails.paymentSummary.paidAmount > 0}
            deletePlanTriggerProps={deletePlanTriggerProps}
            isMobile={isMobile}
          />
        </div>
        <div
          css={css`
            display: flex;
            flex-flow: row;
            gap: ${theme.spacing(isMobile ? 2 : 4)};
            margin-bottom: ${theme.spacing(4)};
            ${isMobile && 'flex-wrap: wrap;'}
          `}
        >
          <div>
            <PaymentPlanSummaryLabel isMobile={isMobile}>{t('Remaining Balance')}</PaymentPlanSummaryLabel>
            <Text
              weight='bold'
              css={css`
                font-size: ${theme.fontSize(24)};
              `}
            >
              {formatCentsToCurrency(planDetails?.paymentSummary.remainingAmount || 0)}
            </Text>
          </div>
          <div>
            <PaymentPlanSummaryLabel isMobile={isMobile}>{t('Next Payment')}</PaymentPlanSummaryLabel>
            <Text
              weight='bold'
              css={css`
                font-size: ${theme.fontSize(24)};
              `}
            >
              {getNextPayment(planDetails)}
            </Text>
          </div>
          <div>
            <PaymentPlanSummaryLabel isMobile={isMobile}>{t('Next Payment Due')}</PaymentPlanSummaryLabel>
            <Text
              weight='bold'
              css={css`
                font-size: ${theme.fontSize(24)};
              `}
            >
              {isoDateToDisplayLocalString(planDetails?.schedule.nextPaymentAt || '')}
            </Text>
          </div>
        </div>
        <div>
          <Text
            weight='bold'
            css={css`
              font-size: ${theme.fontSize(18)};
              margin-bottom: ${theme.spacing(2)};
            `}
          >
            {t('Payment Plan Details')}
          </Text>
          <div
            css={css`
              display: flex;
              flex-flow: row;
              gap: ${theme.spacing(3)};
            `}
          >
            <div
              css={css`
                display: flex;
                flex-flow: column;
                gap: ${theme.spacing(1)};
                ${!isMobile
                  ? `
                  p {
                    font-weight: 600;
                    font-size: ${theme.fontSize(14)};
                    color: ${theme.colors.neutral60};
                    }
                    `
                  : `
                    flex: 1;
                `}
              `}
            >
              <Text color={'subdued'}>{t('Payment plan started')}</Text>
              <Text color={'subdued'}>{t('Date of final payment')}</Text>
              <Text color={'subdued'}>{t('Original balance')}</Text>
              <Text color={'subdued'}>{t('Total payments in plan')}</Text>
              <Text color={'subdued'}>{t('Frequency')}</Text>
              <Text color={'subdued'}>{t('Payments remaining')}</Text>
              <Text color={'subdued'}>{t('Billing method')}</Text>
            </div>
            <div
              css={css`
                display: flex;
                flex-flow: column;
                gap: ${theme.spacing(1)};
                p {
                  font-weight: 600;
                  font-size: ${theme.fontSize(14)};
                  color: ${theme.colors.neutral80};
                }
                ${isMobile &&
                `
                  align-items: flex-end;
                  p {
                    font-weight: 400;
                    font-size: ${theme.fontSize(16)};
                  }
                `}
              `}
            >
              <Text>{isoDateToDisplayLocalString(planDetails.schedule.startAt)}</Text>
              <Text>{isoDateToDisplayLocalString(planDetails.schedule.finalPaymentAt)}</Text>
              <Text>{formatCentsToCurrency(planDetails.schedule.amount)}</Text>
              <Text>{planDetails.schedule.paymentTerm}</Text>
              <Text>
                {prettify(planDetails?.schedule?.frequency || 'N/A', { split: '_', outputCase: 'Sentence case' })}
              </Text>
              <Text>{hasRemainingBalance(planDetails.status) ? planDetails.paymentSummary.remainingTerms : 'N/A'}</Text>
              <Text>{<PaymentMethodName paymentMethod={planDetails.paymentMethod} />}</Text>
            </div>
          </div>
        </div>
      </div>
      <EditPaymentPlanModal
        modalProps={{ ...editPlanModalProps, maxWidth: 600 }}
        type={isPendingCustomerApproval || !isPausedPlan ? 'edit' : 'resume'}
        planDetails={planDetails}
        onPlanUpdated={onPlanUpdated}
      />
      <Modal {...editBillingModalProps} minWidth={600} maxWidth={600}>
        <EditBillingMethodModal
          handleSetBillingMethod={handleSetBillingMethod}
          {...editBillingModalProps}
          {...editBillingMethodProps}
        />
      </Modal>
      <Modal {...pausePlanModalProps} maxWidth={600}>
        <PausePlanModal {...pausePlanModalProps} onPlanPaused={onPlanPaused} planId={planDetails.id} />
      </Modal>
      <Modal {...oneTimePaymentModalProps} maxWidth={600}>
        <StripeElementsWrapper type={PaymentsFlowType.CardOnFilePayment} locationId={planDetails.locationId}>
          <OneTimePaymentModal {...oneTimePaymentModalProps} planDetails={planDetails} />
        </StripeElementsWrapper>
      </Modal>
      <Modal {...deletePlanModalProps} maxWidth={600}>
        <DeletePlanModal
          {...deletePlanModalProps}
          planId={planDetails.id}
          onClose={deletePlanModalProps.onClose}
          onComplete={onPlanDeleted}
        />
      </Modal>
      <Modal {...cancelPlanModalProps} maxWidth={600}>
        <CancelPaymentPlanModal
          planId={planDetails.id}
          planName={planDetails.name}
          locationId={planDetails.locationId}
          onClose={cancelPlanModalProps.onClose}
        />
      </Modal>
    </>
  );
};
